import React, { memo, useMemo, type ReactNode } from 'react'
import { Tab, Tabs, usePageWidth } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import type { PID, TempleRollupStatus } from '@fs/zion-tree-types'
import { useNonReligiousMode, usePermission } from '@fs/zion-permissions'
import { useFeatureFlag } from '@fs/zion-flags'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { usePersonPageParams } from '../personPageRouteUtil'
import useFocusPerson from '../../data-service/useFocusPerson'
import { useTree2TreeMatches } from '../../data-service/useTree2TreeMatches'
import OrdinanceBadge from './OrdinanceBadge'
import useActiveRole from '../../common/useActiveRole'
import { useResearchHelpContext } from '../contexts/ResearchHelpContext'
import useSourceCentricDetailsFlag from '../../data-service/useSourceCentricDetailsFlag'

type Props = {
  showTemple: boolean
}

type FocusPerson = NonNullable<ReturnType<typeof useFocusPerson>['focusPerson']>

type TabRenderContext = {
  t: ReturnType<typeof useTranslation>[0]
  pid: PID
  focusPerson: FocusPerson
  isFSAdmin: boolean
  ordinanceLabel: string | false
  hintCount: number
}

type TabConfig = {
  key: string
  getPath: (ctx: TabRenderContext) => string
  getTitle: (ctx: TabRenderContext) => string | ReactNode
  getLinkName: (isMobile: boolean) => string
  getAriaLabel?: (ctx: TabRenderContext) => string | undefined
  getTestId?: (isMobile: boolean) => string | undefined
  mobileOnly?: boolean
  desktopOnly?: boolean
  showWhenDeleted?: boolean
}

/* eslint-disable sonarjs/no-duplicate-string -- it is ok to duplicate strings for translation keys */
const ordinanceLabelKeyMap: Record<Exclude<TempleRollupStatus, 'CALCULATING'>, string> = {
  READY: 'tree-person.ordinance-tab.ready.aria',
  RESERVED_SHARED_READY: 'tree-person.ordinance-tab.ready.aria',
  RESERVED: 'tree-person.ordinance-tab.ready.aria',
  IN_PROGRESS: 'tree-person.ordinance-tab.ready.aria',
  NEEDS_MORE_INFORMATION: 'tree-person.ordinance-tab.not-ready.aria',
  COMPLETED: 'tree-person.ordinance-tab.completed.aria',
  NOT_AVAILABLE: 'tree-person.ordinance-tab.not-available.aria',
}
/* eslint-enable sonarjs/no-duplicate-string -- re-enable */

// Shared tab definitions reused across both standard and source-centric configs

const ABOUT_TAB: TabConfig = {
  key: 'about',
  getPath: ({ pid }) => `/about/${pid}`,
  getTitle: ({ t }) => t('tree-person.about.label'),
  getLinkName: (isMobile) => (isMobile ? 'Mobile About Tab' : 'Desktop About Tab'),
  getTestId: (isMobile) => (isMobile ? undefined : 'about-tab'),
}

const MEMORIES_TAB: TabConfig = {
  key: 'memories',
  getPath: ({ pid }) => `/memories/${pid}`,
  getTitle: ({ t, focusPerson }) => `${t('tree-person.memories.label')} (${focusPerson.memoryCount})`,
  getLinkName: (isMobile) => (isMobile ? 'Mobile Memories Tab' : 'Desktop Memories Tab'),
  getTestId: (isMobile) => (isMobile ? undefined : 'memories-tab'),
  showWhenDeleted: true,
}

const ORDINANCES_TAB: TabConfig = {
  key: 'ordinances',
  getPath: ({ pid }) => `/ordinances/${pid}`,
  getTitle: ({ t, focusPerson, ordinanceLabel, isFSAdmin }) => {
    const showOrdinanceBadge = !focusPerson.living || isFSAdmin
    return (
      <span>
        {showOrdinanceBadge && (
          <OrdinanceBadge status={focusPerson.templeRollupStatus} label={ordinanceLabel || undefined} />
        )}
        {t('tree-person.ordinances.label')}
      </span>
    )
  },
  getAriaLabel: ({ focusPerson, ordinanceLabel, isFSAdmin }) => {
    const showOrdinanceBadge = !focusPerson.living || isFSAdmin
    return showOrdinanceBadge ? ordinanceLabel || undefined : undefined
  },
  getLinkName: (isMobile) => (isMobile ? 'Mobile Ordinances Tab' : 'Desktop Ordinances Tab'),
  getTestId: (isMobile) => (isMobile ? undefined : 'ordinances-tab'),
}

const VITALS_TAB: TabConfig = {
  key: 'vitals',
  getPath: ({ pid }) => `/vitals/${pid}`,
  getTitle: ({ t }) => t('tree-person.vitals.label'),
  getLinkName: () => 'Mobile Vitals Tab',
  mobileOnly: true,
  showWhenDeleted: true,
}

const NON_VITALS_TAB: TabConfig = {
  key: 'non-vitals',
  getPath: ({ pid }) => `/non-vitals/${pid}`,
  getTitle: ({ t }) => t('tree-person.other.label'),
  getLinkName: () => 'Mobile Non-Vitals Tab',
  mobileOnly: true,
  showWhenDeleted: true,
}

const FAMILY_TAB: TabConfig = {
  key: 'family',
  getPath: ({ pid }) => `/family/${pid}`,
  getTitle: ({ t }) => t('tree-person.family.header'),
  getLinkName: () => 'Mobile Family Tab',
  mobileOnly: true,
}

const DETAILS_TAB: TabConfig = {
  key: 'details',
  getPath: ({ pid }) => `/details/${pid}`,
  getTitle: ({ t }) => t('tree-person.details.label'),
  getLinkName: () => 'Desktop Details Tab',
  getTestId: () => 'details-tab',
  desktopOnly: true,
  showWhenDeleted: true,
}

const STANDARD_TAB_CONFIGS: TabConfig[] = [
  ABOUT_TAB,
  VITALS_TAB,
  NON_VITALS_TAB,
  FAMILY_TAB,
  DETAILS_TAB,
  {
    key: 'sources',
    getPath: ({ pid }) => `/sources/${pid}`,
    getTitle: ({ t, focusPerson }) => `${t('tree-person.sources.label')} (${focusPerson.sourceCount})`,
    getLinkName: (isMobile) => (isMobile ? 'Mobile Sources Tab' : 'Desktop Sources Tab'),
    getTestId: (isMobile) => (isMobile ? undefined : 'sources-tab'),
  },
  {
    key: 'collaborate',
    getPath: ({ pid }) => `/collaborate/${pid}`,
    getTitle: ({ t, focusPerson }) => t('tree-person.collaborate-count.label', { count: focusPerson.collaborateCount }),
    getLinkName: (isMobile) => (isMobile ? 'Mobile Collaborate Tab' : 'Desktop Collaborate Tab'),
    getTestId: () => 'collaborate-tab',
  },
  MEMORIES_TAB,
  {
    key: 'timeline',
    getPath: ({ focusPerson }) => `/timeline/${focusPerson.id}`,
    getTitle: ({ t }) => t('tree-person.timeline.header'),
    getLinkName: (isMobile) => (isMobile ? 'Mobile Timeline Tab' : 'Desktop Timeline Tab'),
    getTestId: () => 'time-line-tab',
  },
  ORDINANCES_TAB,
]

const HINTS_TAB: TabConfig = {
  key: 'research-help',
  getPath: ({ pid }) => `/research-help/${pid}`,
  getTitle: ({ t, hintCount }) => `${t('tree-person.hints.label', { count: hintCount })}`,
  getLinkName: (isMobile) => (isMobile ? 'Mobile Hints Tab' : 'Desktop Hints Tab'),
  getTestId: () => 'hints-tab',
}

const SOURCE_CENTRIC_TAB_CONFIGS: TabConfig[] = [
  VITALS_TAB,
  { ...HINTS_TAB, mobileOnly: true },
  NON_VITALS_TAB,
  FAMILY_TAB,
  {
    key: 'sources',
    getPath: ({ pid }) => `/sources/${pid}`,
    getTitle: ({ t, focusPerson }) => `${t('tree-person.sources.label')} (${focusPerson.sourceCount})`,
    getLinkName: () => 'Mobile Sources Tab',
    mobileOnly: true,
  },
  DETAILS_TAB,
  { ...HINTS_TAB, desktopOnly: true },
  MEMORIES_TAB,
  ORDINANCES_TAB,
  ABOUT_TAB,
]

function PersonPageTabs({ showTemple }: Props): JSX.Element | null {
  const sourceCentric = !!useSourceCentricDetailsFlag().data?.isOn
  const [t] = useTranslation()
  const pageWidth = usePageWidth()
  const { role } = useActiveRole()
  const isFSAdmin = !!role
  const isMobile = pageWidth({ lg: false, default: true })
  const { pid, subPage } = usePersonPageParams('full')
  const { focusPerson } = useFocusPerson()
  const { researchHelpData } = useResearchHelpContext()
  const { isOn: showTree2TreeHints } = useFeatureFlag('treeWeb_tree2tree_hints')
  const { currentTreeId } = useGroupsContext()
  const { data: tree2TreeMatches } = useTree2TreeMatches({
    personId: showTree2TreeHints && sourceCentric ? focusPerson?.id : undefined,
    treeId: currentTreeId ?? 'PRIVATE',
    type: 'PERSON',
  })

  const [nonReligiousModeCheck, status, error] = useNonReligiousMode()
  const nonReligiousMode = status === 'loading' || error !== null || nonReligiousModeCheck

  const [isCountryEnabled, isCountryEnabledStatus] = usePermission('CanAddPersonalDataPermission', 'FamilyTree')
  const memoriesEnabled = isCountryEnabledStatus === 'loaded' && isCountryEnabled

  const visibleConfigs = useMemo(() => {
    if (!focusPerson) return []

    const configs = sourceCentric ? SOURCE_CENTRIC_TAB_CONFIGS : STANDARD_TAB_CONFIGS

    const visibleOnCurrentBreakpoint = (tab: TabConfig): boolean => {
      if (tab.mobileOnly) return isMobile
      if (tab.desktopOnly) return !isMobile
      return true
    }
    const visibleForPersonState = (tab: TabConfig): boolean => !focusPerson.deleted || !!tab.showWhenDeleted

    return configs
      .filter(visibleOnCurrentBreakpoint)
      .filter(visibleForPersonState)
      .filter((tab) => {
        if (tab.key === 'memories') {
          // mobile deleted always shows memories; desktop deleted only if memoriesEnabled
          if (focusPerson.deleted) return isMobile || memoriesEnabled
          return memoriesEnabled
        }
        if (tab.key === 'ordinances') {
          return !focusPerson.deleted && showTemple && !nonReligiousMode && !focusPerson.researcherTree
        }
        return true
      })
  }, [focusPerson, isMobile, memoriesEnabled, nonReligiousMode, showTemple, sourceCentric])

  const tabs = useMemo(() => {
    if (!focusPerson) return []

    const ordinanceLabelKey = focusPerson.templeRollupStatus && ordinanceLabelKeyMap[focusPerson.templeRollupStatus]
    const ordinanceLabel = ordinanceLabelKey && t(ordinanceLabelKey)
    const hintCount = researchHelpData.count + (showTree2TreeHints ? (tree2TreeMatches?.length ?? 0) : 0)
    const ctx: TabRenderContext = { t, pid, focusPerson, isFSAdmin, ordinanceLabel, hintCount }

    return visibleConfigs.map((tab) => (
      <Tab
        key={`${tab.key}-tab`}
        linkName={tab.getLinkName(isMobile)}
        to={tab.getPath(ctx)}
        // @ts-expect-error -- OrdinanceBadge requires JSX title which Tab doesn't officially support
        title={tab.getTitle(ctx)}
        aria-label={tab.getAriaLabel?.(ctx)}
        data-testid={tab.getTestId?.(isMobile)}
      />
    ))
  }, [
    focusPerson,
    isFSAdmin,
    isMobile,
    pid,
    researchHelpData.count,
    showTree2TreeHints,
    t,
    tree2TreeMatches?.length,
    visibleConfigs,
  ])

  const selected = visibleConfigs.findIndex((config) => config.key === subPage)

  // This scrolls to the top of the page each time the tab changes
  const handleTabChange = (): void => {
    document.querySelector('header')?.scrollIntoView({ behavior: 'smooth' })
  }

  if (!focusPerson) return null

  return (
    <Tabs
      key={sourceCentric ? 'source-centric' : 'standard'}
      centered
      index={selected}
      data-testid="person-page-tabs"
      onChange={handleTabChange}
    >
      {tabs}
    </Tabs>
  )
}

export default memo(PersonPageTabs)
